// Reglas del intento de MetaMap del paso 02: etapa, QR, stepper, poll y avisos. Sin React: las usan
// el poll de `kyc/status`, el hook `useMetamapAttempt` y el panel.
import { KycAttemptStageEnum } from '@fiado/type-kit/bin/retailWizard/enums/KycAttemptStageEnum';
import { proofStillOpen } from './proofOfAddress';
import type { Step02RequestCopy } from './step02Request';
import type { KycAttemptProgress, KycModality, KycState, MetamapFlow } from './types';

export const ATTEMPT_POLL_MS = 3000;
export const ATTEMPT_REVIEW_POLL_MS = 15_000;
export const STUCK_IN_STARTED_MS = 5 * 60_000;
/** Sesión previa al despliegue: 4 s las primeras vueltas y luego 10 s. */
export const LEGACY_POLL_FAST_MS = 4000;
export const LEGACY_POLL_SLOW_MS = 10_000;
export const LEGACY_POLL_FAST_ROUNDS = 15;
/** `code` del 409 del prepare cuando la sesión ya está VERIFIED: se relee y se avanza. */
export const KYC_ALREADY_VERIFIED = 'KYC_ALREADY_VERIFIED';

const S = KycAttemptStageEnum;

const STAGE_RANK: Record<KycAttemptStageEnum, number> = {
    [S.AWAITING_START]: 0,
    [S.STARTED]: 1,
    [S.INPUTS_COMPLETED]: 2,
    [S.EXPIRED]: 3,
    [S.RESULT]: 4,
};

/** Los 4 pasos de la tarea; los dos últimos se encienden juntos con INPUTS_COMPLETED. */
export const ATTEMPT_STEPS = ['Esperando', 'Iniciado', 'Datos enviados', 'Validando'] as const;

export type AttemptStepStatus = 'done' | 'current' | 'pending';

/** A qué flujo le pregunta el poll según lo que el paso tiene a la vista. */
export interface KycPollScope {
    kycFlow: 'identity' | 'facematch';
    /** El KYC espera veredicto (WAITING) o está en revisión (REVIEW). */
    kycWaiting: boolean;
    /** La pantalla del comprobante es alcanzable desde el estado del paso. */
    canAskForAddress: boolean;
}

/** Intento abierto: ni veredicto ni vencimiento. */
export function attemptInFlight(stage: KycAttemptStageEnum | null): boolean {
    return stage !== null && stage !== S.RESULT && stage !== S.EXPIRED;
}

/** Hay veredicto pero todavía es revisión: identidad REVIEW, selfie INCONCLUSIVE o comprobante abierto. */
export function attemptInReview(flow: MetamapFlow, data: KycState | undefined): boolean {
    const progress = data?.progress?.[flow] ?? null;
    if (progress?.stage !== S.RESULT) return false;
    if (flow === 'proofOfAddress') {
        return data?.proofOfAddress ? proofStillOpen(data.proofOfAddress) : false;
    }
    return data?.status === 'REVIEW' || data?.status === 'WAITING';
}

/** El flujo cuyo intento decide el poll: el comprobante si falta domicilio, si no el KYC en espera. */
export function polledFlow(data: KycState | undefined, scope: KycPollScope): MetamapFlow | null {
    if (scope.canAskForAddress && data?.addressRequired === true) return 'proofOfAddress';
    return scope.kycWaiting ? scope.kycFlow : null;
}

/** Desde cuándo cuenta el tope: el `attemptStartedAt` del intento abierto o en revisión. */
export function attemptCapStart(
    data: KycState | undefined,
    flow: MetamapFlow | null,
): string | null {
    if (flow === null) return null;
    const progress = data?.progress?.[flow] ?? null;
    if (progress === null) return null;
    return attemptInFlight(progress.stage) || attemptInReview(flow, data)
        ? progress.attemptStartedAt
        : null;
}

/** Intento que abrió un prepare, con el momento en que se pidió. */
export interface AwaitedAttempt {
    flow: MetamapFlow;
    attemptId: string;
    awaitedAt: string;
}

/** Sesión previa al despliegue: el KYC espera y el silo no trae intento. */
export function legacyKycWait(data: KycState | undefined, flow: MetamapFlow | null): boolean {
    if (flow === null || flow === 'proofOfAddress') return false;
    return (data?.progress?.[flow] ?? null) === null && data?.status === 'WAITING';
}

/** Inicio del tope: el prepare mientras el poll no trae su intento, el montaje sin intento, o el del silo. */
export function pollCapStart(
    data: KycState | undefined,
    flow: MetamapFlow | null,
    awaited: AwaitedAttempt | null,
    legacySince: string,
): string | null {
    if (awaited && data?.progress?.[awaited.flow]?.attemptId !== awaited.attemptId) {
        return awaited.awaitedAt;
    }
    if (legacyKycWait(data, flow)) return legacySince;
    return attemptCapStart(data, flow);
}

/** Desde cuándo cuenta el aviso de «¿se atoró?»: el `stageAt` de STARTED. */
export function stuckClockStart(progress: KycAttemptProgress | null): string | null {
    return progress?.stage === S.STARTED ? progress.stageAt : null;
}

/** 3 s con el intento abierto, 15 s en revisión y nada al cerrar, vencer o llegar al tope; 4/10 s sin intento. */
export function kycPollInterval(
    data: KycState | undefined,
    flow: MetamapFlow | null,
    timedOut: boolean,
    rounds = 0,
): number | false {
    if (flow === null || timedOut) return false;
    if (legacyKycWait(data, flow)) {
        return rounds < LEGACY_POLL_FAST_ROUNDS ? LEGACY_POLL_FAST_MS : LEGACY_POLL_SLOW_MS;
    }
    const progress = data?.progress?.[flow] ?? null;
    if (progress === null) return false;
    if (attemptInFlight(progress.stage)) return ATTEMPT_POLL_MS;
    return attemptInReview(flow, data) ? ATTEMPT_REVIEW_POLL_MS : false;
}

/** Estado de cada paso del stepper; `null` en RESULT y EXPIRED, que tienen pantalla propia. */
export function attemptStepStatuses(stage: KycAttemptStageEnum): AttemptStepStatus[] | null {
    switch (stage) {
        case S.AWAITING_START:
            return ['current', 'pending', 'pending', 'pending'];
        case S.STARTED:
            return ['done', 'current', 'pending', 'pending'];
        case S.INPUTS_COMPLETED:
            return ['done', 'done', 'done', 'current'];
        default:
            return null;
    }
}

/** El QR se ve esperando el inicio; en STARTED solo si el vendedor lo pidió otra vez. */
export function qrVisible(progress: KycAttemptProgress | null, revealed: boolean): boolean {
    if (progress?.modality !== 'CUSTOMER_LINK' || !progress.link) return false;
    if (progress.stage === S.AWAITING_START) return true;
    return progress.stage === S.STARTED && revealed;
}

/** QR no disponible: el intento es por QR y el silo no entregó liga. */
export function linkMissing(progress: KycAttemptProgress | null): boolean {
    return (
        progress?.modality === 'CUSTOMER_LINK' &&
        !progress.link &&
        progress.stage === S.AWAITING_START
    );
}

/** Cambiar de método o relanzar descarta lo que el cliente ya empezó: se confirma. */
export function switchNeedsConfirmation(stage: KycAttemptStageEnum | null): boolean {
    return stage === S.STARTED || stage === S.INPUTS_COMPLETED;
}

/** El `onStarted` de la tablet adelanta STARTED mientras llega el webhook; nunca retrocede. */
export function effectiveStage(
    progress: KycAttemptProgress | null,
    startedFor: string | null,
): KycAttemptStageEnum | null {
    if (progress === null) return null;
    if (startedFor === progress.attemptId && progress.stage === S.AWAITING_START) return S.STARTED;
    return progress.stage;
}

/** Tablet: el cliente cerró el SDK sin terminar y todavía no subió su información. */
export function clientLeftSdk(
    exitedFor: string | null,
    progress: KycAttemptProgress | null,
    stage: KycAttemptStageEnum | null,
): boolean {
    if (progress === null || stage === null || exitedFor !== progress.attemptId) return false;
    return STAGE_RANK[stage] < STAGE_RANK[S.INPUTS_COMPLETED];
}

/** QR: el cliente lleva 5 min en STARTED sin avanzar. */
export function clientLooksStuck(
    progress: KycAttemptProgress | null,
    stage: KycAttemptStageEnum | null,
    msInStarted: number | null,
): boolean {
    if (progress?.modality !== 'CUSTOMER_LINK' || stage !== S.STARTED) return false;
    return msInStarted !== null && msInStarted >= STUCK_IN_STARTED_MS;
}

const NOTIFY_WHEN_DONE = 'Te avisamos aquí al terminar';

/** Título y subtexto de la etapa; se anuncian juntos con `aria-live`. */
export interface AttemptHeadline {
    title: string;
    sub: string;
}

export type HeadlineCopy = Pick<
    Step02RequestCopy,
    'awaitingTitle' | 'validatingTitle' | 'scanTitle' | 'scanSub' | 'handTabletSub'
>;

/** «Empezó hace N min»; antes del primer minuto, «hace un momento». */
export function startedAgoText(msSinceStart: number | null): string {
    if (msSinceStart === null || msSinceStart < 60_000) return 'Empezó hace un momento';
    return `Empezó hace ${String(Math.floor(msSinceStart / 60_000))} min`;
}

/** Qué dice el panel en cada etapa: una instrucción corta y un subtexto. */
export function attemptHeadline(
    stage: KycAttemptStageEnum | null,
    modality: KycModality | null,
    copy: HeadlineCopy,
    { qrShown, msSinceStart }: { qrShown: boolean; msSinceStart: number | null },
): AttemptHeadline {
    if (stage === S.STARTED) {
        return {
            title:
                modality === 'CUSTOMER_LINK'
                    ? 'El cliente está verificando en su celular'
                    : 'El cliente está verificando en esta tablet',
            sub: `${startedAgoText(msSinceStart)} · ${NOTIFY_WHEN_DONE}`,
        };
    }
    if (stage === S.INPUTS_COMPLETED) {
        return {
            title: copy.validatingTitle,
            sub: `Ya mandó su información · ${NOTIFY_WHEN_DONE}`,
        };
    }
    if (stage === S.AWAITING_START && modality === 'STORE_DEVICE') {
        return { title: 'Entrega la tablet al cliente', sub: copy.handTabletSub };
    }
    if (stage === S.AWAITING_START && qrShown) {
        return { title: copy.scanTitle, sub: copy.scanSub };
    }
    return { title: copy.awaitingTitle, sub: 'Muestra el QR o usa esta tablet.' };
}
